import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, titleMRClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, getRowStyleFlags, IndexParameterCell, onGridReady } from "src/utils/grid/gridStyleUtils";
import { useTQC2050_R01Store } from "./Store_TQC2050_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { defaultTQC2050_R01DetailDto, TQC2050_R01DetailDto } from "./Dto_TQC2050_R01";

import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { getItemPopupList } from "src/apis/itemApi";
import { ItemDto } from "src/dtos/item/itemDto";
import { showWarning, showSuccess } from "src/utils/toast/toastUtil";
import { getCommonCodeColumnOptions } from "src/utils/grid/GridColumnContent";

// 범용 훅 임포트
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";
import { useDetailTableWithItemSearch } from "src/hooks/detail-table/useDetailTableWithItemSearch";

import { createModuleConfig } from "src/utils/detail-table/detailTableUtils";
import { calculateDetailAmount } from "src/utils/calculation/calculationUtils";
import { useMouseContent } from "src/utils/mouse/mouseContent";
import { useCodeSearch } from "src/hooks/useCodeSearch";

const COLUMN_STATE_KEY = 'TQC2050_R01-detail-grid-column-state-2';

interface Props {
	detailGridRef: React.RefObject<AgGridReact<TQC2050_R01DetailDto> | null>;
}

export const DetailTableSecondary_TQC2050_R01 = (props: Props) => {

	const { detailGridRef } = props;

	const { 
		editTQC2050_R01, setEditTQC2050_R01,
		TQC2050_R01DetailList, // DetailTable의 데이터를 참조
		TQC2050_R01DetailList2, setTQC2050_R01DetailList2, 
		selectedTQC2050_R01DetailList2, setSelectedTQC2050_R01DetailList2, removeSelectedTQC2050_R01DetailList2, addSelectedTQC2050_R01DetailList2, initSelectedTQC2050_R01DetailList2,
		deletedTQC2050_R01DetailList, // DetailTable의 삭제된 행 목록
		deletedTQC2050_R01DetailList2, setDeletedTQC2050_R01DetailList2, addDeletedTQC2050_R01DetailList2, removeDeletedTQC2050_R01DetailList2,
		insertTQC2050_R01DetailList2, updateTQC2050_R01DetailList2, removeTQC2050_R01DetailList2,
		detailColumnState2, setDetailColumnState2,
		originalTQC2050_R01DetailList2, setOriginalTQC2050_R01DetailList2,
		prevSampleCount2, setPrevSampleCount2,
		isEditing2, setIsEditing2,
		detailScrollTop2, setDetailScrollTop2,
	} = useTQC2050_R01Store();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef: detailGridRef,
		key: 'valueIndex',
		gridData: TQC2050_R01DetailList2,
		defaultGridData: defaultTQC2050_R01DetailDto,
		selectedRow: selectedTQC2050_R01DetailList2,
		originalGridData: originalTQC2050_R01DetailList2,
		updateRow: (index: number, data: any) => updateTQC2050_R01DetailList2(index, data),
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		testValue: '시험결과',
		statItem: '순번',
		statResult: '분석결과',
	}

	const createCellClass = (params: any) => {	
		const isSelected = selectedTQC2050_R01DetailList2.includes(params.data.valueIndex);
		const isDeleted = deletedTQC2050_R01DetailList2.includes(params.data.valueIndex);
		
		if (isDeleted) return 'deleted-cell';
		if (isSelected) return 'selected-cell';
		return '';
	}

	// 통계 계산 함수 - DetailTable의 데이터를 참조
	const calculateStatistics = useCallback(() => {
		// 삭제된 행을 제외한 활성 행의 testValue 값 추출 (DetailTable의 데이터 사용)
		const deletedSet = new Set(deletedTQC2050_R01DetailList.map(v => String(v)));
		const activeDetails = TQC2050_R01DetailList.filter(d => !deletedSet.has(String(d.valueIndex)));
		
		// testValue를 숫자로 변환 (유효한 숫자만)
		const values = activeDetails
			.map(d => {
				const num = parseFloat(d.testValue || '');
				return isNaN(num) ? null : num;
			})
			.filter((v): v is number => v !== null);

		if (values.length === 0) {
			return {
				max: null,
				min: null,
				range: null,
				average: null,
				stdDev: null,
				coefficient: null,
			};
		}

		const max = Math.max(...values);
		const min = Math.min(...values);
		const range = max - min;
		const average = values.reduce((sum, val) => sum + val, 0) / values.length;
		
		// 표준편차 계산
		const variance = values.reduce((sum, val) => sum + Math.pow(val - average, 2), 0) / values.length;
		const stdDev = Math.sqrt(variance);
		
		// 변동계수 계산 (평균이 0이 아닐 때만)
		const coefficient = average !== 0 ? (stdDev / average) * 100 : null;

		return {
			max: max.toFixed(2),
			min: min.toFixed(2),
			range: range.toFixed(2),
			average: average.toFixed(2),
			stdDev: stdDev.toFixed(2),
			coefficient: coefficient !== null ? coefficient.toFixed(2) : null,
		};
	}, [TQC2050_R01DetailList, deletedTQC2050_R01DetailList]);

	// 통계 데이터 생성
	const statisticsData = useMemo(() => {
		const stats = calculateStatistics();
		const data = [
			{ statItem: '최대값', statResult: stats.max || '' },
			{ statItem: '최소값', statResult: stats.min || '' },
			{ statItem: '범위', statResult: stats.range || '' },
			{ statItem: '평균값', statResult: stats.average || '' },
			{ statItem: '표준편차', statResult: stats.stdDev || '' },
			{ statItem: '변동계수', statResult: stats.coefficient || '' },
		];
		// console.log('통계 데이터:', data);
		// console.log('통계 계산 결과:', stats);
		// console.log('DetailTable 데이터:', TQC2050_R01DetailList);
		return data;
	}, [calculateStatistics, TQC2050_R01DetailList]);

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['testValue', 'remark'];


	// DetailTableSecondary는 통계만 표시하므로 입력 컬럼 불필요
	const baseColumnDefs: ColDef<TQC2050_R01DetailDto>[] = useMemo(() => [
		// ...getDefaultRowIndexColumnConfig(),
		{
			headerName: '순번', colId: 'statItem', field: 'statItem', width: 120,
			cellClass: (params: any) => createCellClass(params),
			editable: false,
		},
		{
			headerName: '분석결과', colId: 'statResult', field: 'statResult', width: 150,
			cellClass: (params: any) => createCellClass(params),
			editable: false,
		},
	], []);

	const columnDefs: ColDef<TQC2050_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState2 === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<TQC2050_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState2,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, detailColumnState2, headerNames]);


	// 품목 검색 함수
	const { searchExactCode } = useCodeSearch({});
	// 공통 상세 테이블 핸들러 - 간단한 설정으로 변경
	const detailTableHandlers = {
		settingCodeSearchModalProps: (field: string, data: any) => {
		}
	};

	const cellEventHandlers = useDetailTableWithItemSearch<TQC2050_R01DetailDto>({
		detailList: TQC2050_R01DetailList2,
		editableColumns: defaultEditableColumns,
		itemSearchConfig: {
			config: {
				category: '품목',
			responseMapping: {
				itemNm: 'testValue',
			},
			clearFields: {
				testValue: "", remark: ""
			},
				searchFunction: (code: string, onSuccess?: (item: any) => void) => 
					searchExactCode('item', code, onSuccess)
			},
			settingCodeSearchModalProps: detailTableHandlers.settingCodeSearchModalProps,
		},
		keyDownConfig: {
			qtyField: 'testValue',
			enableArrowNavigation: true
		}
	});

	const cellDoubleClickActions: CustomCellDoubleClickAction<TQC2050_R01DetailDto>[] = useMemo(() => [
		{
			validate: (field, data) => field === 'testValue',
			action: (e, foundData) => {
				// 더블클릭 시 편집 모드 시작
				e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! });
			}
		},
	], []);


	// 시료수(sampleCount) 변경 시 DetailTable 행 수 동기화
	useEffect(() => {
		if (!editTQC2050_R01 || editTQC2050_R01.sampleCount === undefined || editTQC2050_R01.sampleCount === null) {
			return;
		}

		const targetCount = Number(editTQC2050_R01.sampleCount);
		
		// ✅ Zustand 스토어에서 최신 값 직접 참조 (의존성 배열에 포함하지 않아도 최신 값 참조 가능)
		const storeState = useTQC2050_R01Store.getState();
		const currentPrevSampleCount = storeState.prevSampleCount2;
		const currentIsEditing = storeState.isEditing2;
		
		// ✅ 등록 버튼 클릭 시 즉시 실행되도록 보장: 신규 등록 상태일 때 prevSampleCount 초기화
		const isNewRecord = !editTQC2050_R01.testResultNo || editTQC2050_R01.testResultNo === '';
		
		if (isNewRecord) {
			// 신규 등록 상태이면 prevSampleCount 초기화하여 즉시 실행 보장
			if (currentPrevSampleCount !== undefined) {
				setPrevSampleCount2(undefined);
			}
		}
		
		// ✅ 이전 값과 같으면 실행하지 않음 (무한 루프 방지)
		if (currentPrevSampleCount === targetCount) {
			return;
		}

		// ✅ 입력 중일 때는 sampleCount 동기화 건너뛰기 (하지만 prevSampleCount는 업데이트)
		if (currentIsEditing) {
			if (currentPrevSampleCount !== targetCount) {
				setPrevSampleCount2(targetCount);
			}
			return;
		}

		if (targetCount <= 0) {
			// 시료수가 0 이하면 빈 리스트로 설정
			setTQC2050_R01DetailList2([]);
			setPrevSampleCount2(targetCount);
			return;
		}

		
	}, [editTQC2050_R01?.sampleCount, editTQC2050_R01?.testResultNo, TQC2050_R01DetailList2, deletedTQC2050_R01DetailList2, setTQC2050_R01DetailList2, addDeletedTQC2050_R01DetailList2, setPrevSampleCount2]);

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
	const statisticsGridRef = useRef<AgGridReact<any>>(null);

	return (
		<div className="w-full h-full flex flex-col ag-theme-alpine">
			<Grid_Touch<any>
				ref={statisticsGridRef}
				rowData={statisticsData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={(params) => params.data?.statItem || `stat-${params.data?.statItem || 'row'}`}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={`${COLUMN_STATE_KEY}-statistics`}
				setColumnState={setDetailColumnState2}
				scrollTop={detailScrollTop2}
				setScrollTop={setDetailScrollTop2}
			/>
		</div>
	)
}

export default DetailTableSecondary_TQC2050_R01;
